import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import MapErrorBanner from '../components/MapErrorBanner';
import {
  MapPin, Users, RefreshCw, Signal, Clock, Phone, Radio, 
  Navigation, Circle, User, Shield, AlertTriangle
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Status colors for investigators
const STATUS_COLORS = {
  AVAILABLE: '#10B981',
  ON_ASSIGNMENT: '#3B82F6',
  SURVEILLANCE: '#8B5CF6',
  TRANSPORT: '#F59E0B',
  OFF_DUTY: '#6B7280',
  EMERGENCY: '#EF4444'
};

const STATUS_LABELS = {
  AVAILABLE: 'Available',
  ON_ASSIGNMENT: 'On Assignment',
  SURVEILLANCE: 'Surveillance',
  TRANSPORT: 'Transport',
  OFF_DUTY: 'Off Duty',
  EMERGENCY: 'Emergency'
};

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 32.7767,
  lng: -96.7970 // Dallas, TX
};

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#0e1626' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#283d6a' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] }
];

const InvestigatorTracking = () => {
  const token = localStorage.getItem('nexus_token');
  const [investigators, setInvestigators] = useState([]);
  const [selectedInvestigator, setSelectedInvestigator] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places']
  });

  // Reverse geocode lat/lng → human-readable address
  const resolveAddress = useCallback((lat, lng) => {
    setSelectedAddress('');
    if (!window.google?.maps) { setSelectedAddress('__UNAVAILABLE__'); return; }
    if (!geocoderRef.current) {
      try { geocoderRef.current = new window.google.maps.Geocoder(); }
      catch (e) { setSelectedAddress('__UNAVAILABLE__'); return; }
    }
    try {
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          setSelectedAddress(results[0].formatted_address || '__UNAVAILABLE__');
        } else {
          setSelectedAddress('__UNAVAILABLE__');
        }
      });
    } catch (e) {
      setSelectedAddress('__UNAVAILABLE__');
    }
  }, []);

  const selectInvestigator = useCallback((inv) => {
    setSelectedInvestigator(inv);
    if (inv && inv.latitude && inv.longitude) {
      resolveAddress(inv.latitude, inv.longitude);
    } else {
      setSelectedAddress('');
    }
  }, [resolveAddress]);

  const fetchInvestigators = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/investigators/tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvestigators(data);
        
        // Auto-center on first investigator with location
        const firstWithLoc = data.find(i => i.latitude && i.longitude);
        if (firstWithLoc) {
          setMapCenter({ lat: firstWithLoc.latitude, lng: firstWithLoc.longitude });
        }
      }
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvestigators();
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(fetchInvestigators, 30000);
    return () => clearInterval(interval);
  }, [fetchInvestigators]);

  const handleStatusUpdate = async (investigatorId, newStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/investigators/${investigatorId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      toast({ title: 'Status Updated', description: 'Investigator status has been updated' });
      fetchInvestigators();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const filteredInvestigators = investigators.filter(inv => 
    statusFilter === 'all' || inv.tracking_status === statusFilter
  );

  const investigatorsWithLocation = filteredInvestigators.filter(inv => inv.latitude && inv.longitude);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const centerOnInvestigator = (inv) => {
    if (inv.latitude && inv.longitude && mapRef.current) {
      mapRef.current.panTo({ lat: inv.latitude, lng: inv.longitude });
      mapRef.current.setZoom(15);
      selectInvestigator(inv);
    }
  };

  if (loadError) {
    return (
      <div className="flex items-center justify-center h-96 bg-[#131A2A] rounded-lg border border-[#334155]">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#DC2626] mb-4" />
          <p className="text-[#F8FAFC]">Failed to load Google Maps</p>
          <p className="text-sm text-[#94A3B8]">Please check your API key configuration</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="investigator-tracking-page">
      <MapErrorBanner />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center" style={{ background: 'var(--nx-lime)', color: '#052E16', borderRadius: 10, boxShadow: '0 6px 14px -4px rgba(34,197,94,0.45)' }}>
            <Radio className="w-5 h-5" />
          </div>
          <div>
            <div className="nx-eyebrow">Live Operations</div>
            <h1 className="nx-h1 mt-1">Investigator GPS Tracking</h1>
            <p className="nx-body mt-1">Real-time field team location and status monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-[#0B0F19] border-[#334155] text-[#F8FAFC]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#131A2A] border-[#334155]">
              <SelectItem value="all" className="text-[#F8FAFC]">All Statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key} className="text-[#F8FAFC]">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="border-[#334155] text-[#94A3B8] hover:bg-[#182136]" 
            onClick={fetchInvestigators}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <Card className="bg-[#131A2A] border-[#334155] h-[600px]">
            <CardContent className="p-0 h-full">
              {!isLoaded ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="w-8 h-8 text-[#3B82F6] animate-spin" />
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={11}
                  onLoad={onMapLoad}
                  options={{
                    styles: darkMapStyle,
                    disableDefaultUI: false,
                    zoomControl: true,
                    mapTypeControl: true,
                    streetViewControl: true,
                    fullscreenControl: true
                  }}
                >
                  {investigatorsWithLocation.map(inv => (
                    <Marker
                      key={inv.id}
                      position={{ lat: inv.latitude, lng: inv.longitude }}
                      onClick={() => selectInvestigator(inv)}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 12,
                        fillColor: STATUS_COLORS[inv.tracking_status] || STATUS_COLORS.AVAILABLE,
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 3
                      }}
                    />
                  ))}

                  {selectedInvestigator && selectedInvestigator.latitude && (
                    <InfoWindow
                      position={{ lat: selectedInvestigator.latitude, lng: selectedInvestigator.longitude }}
                      onCloseClick={() => { setSelectedInvestigator(null); setSelectedAddress(''); }}
                    >
                      <div style={{ padding: '8px', minWidth: '240px', maxWidth: '280px', color: '#0F172A' }} data-testid="tracking-info-window">
                        <h4 style={{ fontWeight: 700, color: '#0F172A', fontSize: '14px', margin: 0 }}>
                          {selectedInvestigator.name}
                        </h4>
                        <p style={{ fontSize: '12px', color: '#475569', margin: '2px 0 0 0', textTransform: 'capitalize' }}>
                          {selectedInvestigator.role?.replace(/_/g, ' ')}
                        </p>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            width: '10px', height: '10px', borderRadius: '9999px',
                            backgroundColor: STATUS_COLORS[selectedInvestigator.tracking_status],
                            display: 'inline-block'
                          }} />
                          <span style={{ fontSize: '12px', color: '#0F172A', fontWeight: 600 }}>
                            {STATUS_LABELS[selectedInvestigator.tracking_status]}
                          </span>
                        </div>
                        {/* Physical address (reverse-geocoded) */}
                        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #E2E8F0' }}>
                          <p style={{ fontSize: '10px', color: '#64748B', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>
                            Current Address
                          </p>
                          <p style={{ fontSize: '12px', color: '#0F172A', margin: '2px 0 0 0', lineHeight: 1.35 }}>
                            {!selectedAddress
                              ? 'Looking up address…'
                              : selectedAddress === '__UNAVAILABLE__'
                                ? <span style={{ color: '#64748B', fontStyle: 'italic' }}>Address lookup unavailable</span>
                                : selectedAddress}
                          </p>
                          <p style={{ fontSize: '10px', color: '#94A3B8', fontFamily: 'monospace', margin: '4px 0 0 0' }}>
                            {selectedInvestigator.latitude.toFixed(5)}, {selectedInvestigator.longitude.toFixed(5)}
                          </p>
                        </div>
                        {selectedInvestigator.current_case && (
                          <p style={{ fontSize: '12px', color: '#1E293B', marginTop: '8px', margin: '8px 0 0 0' }}>
                            <strong style={{ color: '#0F172A' }}>Case:</strong> {selectedInvestigator.current_case}
                          </p>
                        )}
                        {selectedInvestigator.last_update && (
                          <p style={{ fontSize: '11px', color: '#64748B', marginTop: '6px', margin: '6px 0 0 0' }}>
                            Updated: {new Date(selectedInvestigator.last_update).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Investigators Panel */}
        <div className="space-y-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-3">
              <CardTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-[#3B82F6]" />
                Field Team ({filteredInvestigators.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[520px] overflow-y-auto nx-inner-scroll-keep lg:max-h-[520px]" data-testid="investigator-list">
              {filteredInvestigators.length === 0 ? (
                <p className="text-[#94A3B8] text-sm text-center py-4">No investigators found</p>
              ) : (
                filteredInvestigators.map(inv => (
                  <div
                    key={inv.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedInvestigator?.id === inv.id 
                        ? 'bg-[#1D4ED8]/20 border-[#1D4ED8]' 
                        : 'bg-[#0B0F19] border-[#334155] hover:border-[#475569]'
                    }`}
                    onClick={() => centerOnInvestigator(inv)}
                    data-testid={`investigator-card-${inv.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: STATUS_COLORS[inv.tracking_status] || STATUS_COLORS.AVAILABLE }}
                        >
                          {inv.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="text-[#F8FAFC] font-medium text-sm">{inv.name}</p>
                          <p className="text-xs text-[#94A3B8]">{inv.role?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      {inv.latitude && inv.longitude && (
                        <Signal className="w-4 h-4 text-[#10B981]" />
                      )}
                    </div>
                    
                    <div className="mt-2 flex items-center gap-2">
                      <Badge 
                        className="text-[10px] px-1.5 py-0.5"
                        style={{ 
                          backgroundColor: `${STATUS_COLORS[inv.tracking_status]}20`,
                          color: STATUS_COLORS[inv.tracking_status],
                          borderColor: STATUS_COLORS[inv.tracking_status]
                        }}
                      >
                        {STATUS_LABELS[inv.tracking_status] || 'Unknown'}
                      </Badge>
                    </div>

                    {inv.current_case && (
                      <p className="text-xs text-[#94A3B8] mt-2 truncate">
                        <span className="text-[#475569]">Case:</span> {inv.current_case}
                      </p>
                    )}

                    {inv.last_update && (
                      <p className="text-[10px] text-[#475569] mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(inv.last_update).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#F8FAFC] text-sm">Status Overview</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_LABELS).map(([status, label]) => {
                const count = investigators.filter(i => i.tracking_status === status).length;
                return (
                  <div 
                    key={status}
                    className="p-2 bg-[#0B0F19] rounded border border-[#334155] text-center"
                  >
                    <div 
                      className="w-3 h-3 rounded-full mx-auto mb-1"
                      style={{ backgroundColor: STATUS_COLORS[status] }}
                    />
                    <p className="text-lg font-bold text-[#F8FAFC]">{count}</p>
                    <p className="text-[9px] text-[#94A3B8] uppercase tracking-wider">{label}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InvestigatorTracking;
